/**
 * RiskHistoryTimeline — exposure trend + cycle-by-cycle stepper for a Risk.
 *
 * Two views are combined on one page:
 *   1. Recharts `LineChart` plotting exposure (and probability / impact) over
 *      successive review cycles.
 *   2. A vertical MUI `Stepper` listing each cycle with the level chip and
 *      impact x probability that was recorded for that cycle.
 *
 * Data source: `riskTrendUrl(riskId)` returning ordered RiskTrendPoint[].
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  Button,
} from "@mui/material";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import HistoryIcon from "@mui/icons-material/History";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import PageTitle from "../../../components/Page Component/PageTitle";
import { fetchData } from "../../../store/actions";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import { riskDetailUrl, riskTrendUrl } from "../../../Service/Apis";
import { Risk, RiskTrendPoint } from "./Types/RiskManagementTypes";
import RiskScoreBadge, { levelColor } from "./components/RiskScoreBadge";
import RiskStatusChip from "./components/RiskStatusChip";
import { normalizeRisk } from "./components/normalizers";

const RiskHistoryTimeline: React.FC = () => {
  const { riskId } = useParams<{ riskId: string }>();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const showToast = useCustomToast();

  const [risk, setRisk] = useState<Risk | null>(null);
  const [trend, setTrend] = useState<RiskTrendPoint[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!riskId) return;
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        dispatch(fetchData(riskDetailUrl(riskId))),
        dispatch(fetchData(riskTrendUrl(riskId))),
      ]);
      setRisk(normalizeRisk(r));
      const rawTrend: any[] =
        (t as any)?.results ?? (t as any)?.trend ?? t ?? [];
      // DRF serializes DecimalField as a string ("3.40"); coerce to number
      // here so chart libs and arithmetic don't blow up downstream.
      const trendData: RiskTrendPoint[] = rawTrend.map((p) => ({
        ...p,
        exposure: Number(p?.exposure ?? 0),
        impact: Number(p?.impact ?? 0),
        probability: Number(p?.probability ?? 0),
      }));
      setTrend(trendData);
    } catch {
      showToast("Failed to load risk history.", "error", false);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskId, dispatch]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 12, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!risk) {
    return (
      <Container maxWidth="md" sx={{ mt: 12 }}>
        <Alert severity="error">Risk not found.</Alert>
      </Container>
    );
  }

  const chartData = trend.map((p) => ({
    cycle: p.cycle_name,
    // Exposure is the raw `impact * probability` (1-100), which is always
    // an integer in practice; round defensively to drop any FP noise.
    Exposure: Math.round(Number(p.exposure ?? 0)),
    Impact: p.impact,
    Probability: p.probability,
  }));

  const activeStep = Math.max(trend.length - 1, 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 6 }}>
      <PageTitle>Risk Trend</PageTitle>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Risk
          </Typography>
          <Typography variant="h6">{risk.description}</Typography>
          <Typography variant="body2" color="text.secondary">
            Area: {risk.area_name ?? "—"} • Contributor:{" "}
            {risk.contributor_name ?? "—"} • Owner:{" "}
            {risk.risk_owner_name ?? "Unassigned"}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              startIcon={<HistoryIcon />}
              onClick={() => navigate(`/pms/risk-management/risk/${risk.id}/audit`)}
            >
              View audit log
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: 420 }}>
            <Typography variant="subtitle1" gutterBottom>
              Exposure across cycles
            </Typography>
            {chartData.length === 0 ? (
              <Typography color="text.secondary">
                No snapshots recorded yet for this risk.
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="cycle" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" domain={[1, 10]} />
                  <RechartsTooltip />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="Exposure"
                    stroke={levelColor("HIGH")}
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="Impact"
                    stroke="#1976d2"
                    strokeDasharray="4 4"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="Probability"
                    stroke="#9c27b0"
                    strokeDasharray="2 4"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, maxHeight: 420, overflow: "auto" }}>
            <Typography variant="subtitle1" gutterBottom>
              Cycle-by-cycle changes
            </Typography>
            {trend.length === 0 ? (
              <Typography color="text.secondary">No history yet.</Typography>
            ) : (
              <Stepper orientation="vertical" activeStep={activeStep}>
                {trend.map((p, idx) => (
                  <Step key={p.cycle_id} expanded>
                    <StepLabel>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {p.cycle_name}
                        </Typography>
                        <RiskScoreBadge level={p.level} exposure={p.exposure} />
                      </Box>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="caption" color="text.secondary">
                        Period ends {new Date(p.period_end).toLocaleDateString()}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          size="small"
                          label={`Impact ${p.impact}`}
                          sx={{ mr: 0.5 }}
                        />
                        <Chip
                          size="small"
                          label={`Probability ${p.probability}`}
                          sx={{ mr: 0.5 }}
                        />
                        <RiskStatusChip status={p.status} />
                      </Box>
                      {idx > 0 && (
                        <Typography
                          variant="caption"
                          color={
                            p.exposure > trend[idx - 1].exposure
                              ? "error.main"
                              : p.exposure < trend[idx - 1].exposure
                              ? "success.main"
                              : "text.secondary"
                          }
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          Δ exposure {Math.round(
                            p.exposure - trend[idx - 1].exposure
                          )}
                        </Typography>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RiskHistoryTimeline;
